import type { CreditDecisionOutcomeSchema } from '@paguz/contracts';
import {
  AgentSessionIdSchema,
  ApplicationIdSchema,
  ApplicationPhaseSchema,
  ApplicationStatusSchema,
  CashFlowAnalysisSchema,
  ConsentSchema,
  CreditApplicationSchema,
  CustomerIdSchema,
  DecisionRecommendationSchema,
  DocumentVerdictSchema,
  EvidenceTypeSchema,
  MoneySchema,
  PhaseChangeSchema,
  PublicAgentSchema,
  RecommendationSchema,
  TimestampSchema,
  paginatedSchema,
} from '@paguz/contracts';
import { z } from 'zod';

/**
 * Vistas de consola de expedientes (`/v1/admin/applications*`, `06-backoffice.md` §4.2).
 * Reutilizan `CreditApplication`, `CashFlowAnalysis` y `DecisionRecommendation` de contracts y
 * agregan lo que solo ve el analista (riesgo, ruta de la política, memo del copiloto, historial).
 * Pendiente: moverlas a `packages/contracts/src/admin/` cuando WS-ORI publique la API.
 */

export const AdminUserRefSchema = z.object({ id: z.string(), name: z.string() });
export type AdminUserRef = z.infer<typeof AdminUserRefSchema>;

/** Por qué el expediente llega (o no) al analista según la política de decisión vigente. */
export const PolicyRouteSchema = z.enum(['AUTO_APPROVE', 'AUTO_REJECT', 'ANALYST']);
export type PolicyRoute = z.infer<typeof PolicyRouteSchema>;

export const AdminApplicationSummarySchema = z.object({
  id: ApplicationIdSchema,
  applicant: z.object({
    customerId: CustomerIdSchema,
    fullName: z.string(),
    maskedDocument: z.string(),
  }),
  businessName: z.string().nullable(),
  businessType: z.string().nullable(),
  city: z.string().nullable(),
  phase: ApplicationPhaseSchema,
  status: ApplicationStatusSchema,
  requestedAmount: MoneySchema.nullable(),
  suggestedLimit: MoneySchema.nullable(),
  recommendation: RecommendationSchema.nullable(),
  policyRoute: PolicyRouteSchema.nullable(),
  alerts: z.int().min(0),
  phaseEnteredAt: TimestampSchema,
  assignedTo: AdminUserRefSchema.nullable(),
});
export type AdminApplicationSummary = z.infer<typeof AdminApplicationSummarySchema>;
export const AdminApplicationPageSchema = paginatedSchema(AdminApplicationSummarySchema);

export const AdminApplicationQuerySchema = z.object({
  phase: ApplicationPhaseSchema.optional(),
  recommendation: RecommendationSchema.optional(),
  withAlerts: z.enum(['true']).optional(),
  assignedTo: z.enum(['me']).optional(),
  cursor: z.string().optional(),
});
export type AdminApplicationQuery = z.infer<typeof AdminApplicationQuerySchema>;

// ── Riesgo: central, KYC y listas ─────────────────────────────────────────
export const BureauReportSchema = z.object({
  provider: z.string(),
  consultedAt: TimestampSchema,
  score: z.int().nullable(),
  /** Calificación de la central (A–E) o `null` si no tiene historia. */
  rating: z.string().nullable(),
  thinFile: z.boolean(),
  currentDelinquencyDays: z.int().min(0),
  inquiriesLast6Months: z.int().min(0),
  totalDebt: MoneySchema,
  monthlyPayments: MoneySchema,
  obligations: z.array(
    z.object({
      entity: z.string(),
      type: z.string(),
      balance: MoneySchema,
      monthlyPayment: MoneySchema,
      status: z.enum(['CURRENT', 'DELINQUENT', 'CLOSED']),
    }),
  ),
});
export type BureauReport = z.infer<typeof BureauReportSchema>;

export const KycResultSchema = z.object({
  provider: z.string(),
  checkedAt: TimestampSchema,
  status: z.enum(['VERIFIED', 'PENDING', 'FAILED']),
  documentMatch: z.boolean(),
  /** Puntaje de prueba de vida 0–1 como string decimal. */
  livenessScore: z.string().nullable(),
  sanctions: z.enum(['CLEAR', 'REVIEW', 'MATCH']),
  listsChecked: z.array(z.string()),
});
export type KycResult = z.infer<typeof KycResultSchema>;

export const ScorecardResultSchema = z.object({
  code: z.string(),
  label: z.string(),
  value: z.string(),
  weight: z.string(),
  points: z.int(),
});

export const CopilotMemoSchema = z.object({
  generatedAt: TimestampSchema,
  model: z.string(),
  summary: z.string(),
  strengths: z.array(z.string()),
  risks: z.array(z.string()),
  questions: z.array(z.string()),
  /** Fuentes citadas (documento, cifra del flujo o artículo de la KB). */
  citations: z.array(z.object({ label: z.string(), ref: z.string() })),
});
export type CopilotMemo = z.infer<typeof CopilotMemoSchema>;

export const ApplicationConversationSchema = z.object({
  sessionId: AgentSessionIdSchema,
  agent: PublicAgentSchema,
  startedAt: TimestampSchema,
  turns: z.int().min(0),
  costUsd: z.string(),
  excerpt: z.array(
    z.object({
      seq: z.int(),
      role: z.enum(['user', 'assistant', 'human_agent', 'system_note']),
      text: z.string(),
      at: TimestampSchema,
    }),
  ),
});
export type ApplicationConversation = z.infer<typeof ApplicationConversationSchema>;

export const ApplicationEventSchema = z.object({
  at: TimestampSchema,
  actor: z.object({
    type: z.enum(['CUSTOMER', 'AGENT', 'ANALYST', 'SYSTEM']),
    name: z.string(),
  }),
  action: z.string(),
  detail: z.string(),
});
export type ApplicationEvent = z.infer<typeof ApplicationEventSchema>;

export const AdminApplicationDetailSchema = z.object({
  application: CreditApplicationSchema,
  applicant: z.object({
    customerId: CustomerIdSchema,
    fullName: z.string(),
    maskedDocument: z.string(),
    maskedPhone: z.string(),
    age: z.int().nullable(),
    city: z.string().nullable(),
  }),
  consents: z.array(ConsentSchema),
  phaseEnteredAt: TimestampSchema,
  phaseHistory: z.array(PhaseChangeSchema),
  assignedTo: AdminUserRefSchema.nullable(),
  cashFlow: CashFlowAnalysisSchema.nullable(),
  risk: z.object({
    bureau: BureauReportSchema.nullable(),
    kyc: KycResultSchema.nullable(),
    scorecard: z.array(ScorecardResultSchema),
  }),
  recommendation: DecisionRecommendationSchema.nullable(),
  policyRoute: z
    .object({ route: PolicyRouteSchema, policyVersion: z.int(), why: z.array(z.string()) })
    .nullable(),
  copilotMemo: CopilotMemoSchema.nullable(),
  conversations: z.array(ApplicationConversationSchema),
  history: z.array(ApplicationEventSchema),
});
export type AdminApplicationDetail = z.infer<typeof AdminApplicationDetailSchema>;

// ── Acciones del analista ─────────────────────────────────────────────────
/** `decisionReasonCodes` (`06-backoffice.md` §4.2). Pendiente de publicarse en contracts. */
export const DECISION_REASON_CODES = {
  APPROVED: [
    ['CAPACITY_SUFFICIENT', 'Capacidad de pago suficiente'],
    ['DOCS_CONSISTENT', 'Soportes coherentes entre sí'],
    ['BUREAU_GOOD', 'Buen comportamiento en central'],
    ['BUSINESS_STABLE', 'Negocio estable y con antigüedad'],
  ],
  COUNTER_OFFER: [
    ['CAPACITY_PARTIAL', 'Capacidad alcanza para un cupo menor'],
    ['SALES_PARTIALLY_SUPPORTED', 'Ventas parcialmente soportadas'],
    ['DEBT_LEVEL_HIGH', 'Endeudamiento alto'],
    ['TERM_ADJUSTED', 'Plazo ajustado a la capacidad'],
  ],
  REJECTED: [
    ['CAPACITY_INSUFFICIENT', 'Capacidad de pago insuficiente'],
    ['DOCS_INCONSISTENT', 'Documentos inconsistentes'],
    ['BUREAU_NEGATIVE', 'Reporte negativo en central'],
    ['SANCTIONS_HIT', 'Coincidencia en listas restrictivas'],
    ['POLICY_AGE_BUSINESS', 'Antigüedad del negocio menor a la política'],
    ['OUT_OF_COVERAGE', 'Fuera de zona de cobertura'],
  ],
} as const satisfies Record<
  z.infer<typeof CreditDecisionOutcomeSchema>,
  readonly (readonly [string, string])[]
>;

export const DecisionResultSchema = z.object({
  detail: AdminApplicationDetailSchema,
  message: z.string(),
});

export const DocumentReviewOverrideSchema = z.object({
  verdict: DocumentVerdictSchema,
  reason: z.string().trim().min(5).max(500),
});

export const RequestDocumentBodySchema = z.object({
  type: EvidenceTypeSchema,
  message: z.string().trim().min(5).max(500),
});
